import { useState, useEffect, FunctionComponent } from 'react';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@lmig/lmds-react-table';
import { Heading } from '@lmig/lmds-react-typography';
import { Link } from '@lmig/lmds-react-link';
import './PolicyLookup.scss';
import { CircularProgressSpinner } from '@lmig/sales-ui-circular-progress-spinner';
import { useUserData } from 'Foundation/User/UserContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { Notification } from '@lmig/lmds-react-notification';
import { saveRecentSearch } from './Helpers/SearchHelper';
import { getBLAxisBaseURL } from 'Foundation/Configurations/GlobalConfiguration';

const renderInsuredNamePart = (name?: string | null) => {
  if (!name) return null;
  const idx = name.indexOf(',');
  if (idx === -1) return name;
  return name.slice(0, idx);
};

const renderInsuredAddressPart = (name?: string | null) => {
  if (!name) return null;
  const idx = name.indexOf(',');
  if (idx === -1) return null;
  return name.slice(idx + 1).trim();
};

const CustomerLookup: FunctionComponent = () => {
  const [customer, setCustomer] = useState<{ data?: IInsuredNameResults[]; isLoaded: boolean }>({ isLoaded: false });
  const { userData } = useUserData();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const [insuredName] = useState<string | null>(params.get('insuredName'));
  const navigate = useNavigate();

  useEffect(() => {
    async function getCustomerData() {
      const response = await fetch(
        `/api/sitecore/CustomerAsync/GetInsuredNameResults?agencyNumber=${userData?.selectedStatCodeData?.SubStat}&searchString=${insuredName}`,
      );
      const result: IInsuredNameWrapper = await response.json();

      if (result?.InsuredData?.length > 0) {
        setCustomer({ data: result.InsuredData, isLoaded: true });
      } else {
        setCustomer({ isLoaded: true });
      }
    }

    if (insuredName && userData?.selectedStatCodeData?.SubStat !== undefined) {
      getCustomerData().catch(console.error);
    }
  }, [insuredName, userData?.selectedStatCodeData?.SubStat, navigate]);

  const renderLoadingOrError = () => {
    if (customer.isLoaded && userData?.selectedStatCodeData?.SubStat === '') {
      return (
        <Notification alert="Please try again later. " highlightType="negative">
          The data for this agency/policy is temporarily unavailable. If this issue persists, please contact Agency
          Interface Support Group at 888-451-8414.
        </Notification>
      );
    }
    if (!customer.isLoaded) {
      return <CircularProgressSpinner size="32" aria-label="Loading customer data..." />;
    }
    if (!customer.data) {
      return <Heading type="h6">No policies found for insured name.</Heading>;
    }
    return null;
  };

  const openPolicyLookup = (policyNumber: string) => {
    navigate(`/search/policy-lookup?policyNumber=${policyNumber}`);
  };

  const renderNewExperience = () => {
    if (!customer.data) return null;
    return (
      <Table className="uscm-recent-activities-table" rowStyle="zebra">
        <TableHeader>
          <TableRow>
            <TableCell type="colHead">Insured Name</TableCell>
            <TableCell type="colHead">Policy</TableCell>
            <TableCell type="colHead">Policy Effective Date</TableCell>
            <TableCell type="colHead">Line of Business</TableCell>
            <TableCell type="colHead">Agency Code</TableCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {customer.data.map((item, i) => (
            <TableRow key={i}>
              <TableCell>
                <div>{renderInsuredNamePart(item.InsuredName)}</div>
                <div>{renderInsuredAddressPart(item.InsuredName)}</div>
              </TableCell>
              <TableCell>
                {item.PolicyNumber && 
                (!userData?.isAISGUser || userData?.isMockingAgent)
                  ? (
                    <Link onClick={() => openPolicyLookup(item.PolicyNumber)}>{item.PolicyNumber}</Link>
                  ) 
                  : (
                    <Link
                      type="button"
                      onClick={() => {
                        const axisBaseUrl = getBLAxisBaseURL();
                        const accountNumberForAxis =
                          item?.PolicyNumber.length > 8 ? item.PolicyNumber.slice(-8) : item.PolicyNumber;
                        const redirectUrl = `${axisBaseUrl}/business/${accountNumberForAxis}/policy/${item.PolicyNumber}`;
                        window.open(redirectUrl, '_blank');
                      }}
                    >
                      {item.PolicyNumber ?? ''}
                    </Link>
                  )
                }
              </TableCell>
              <TableCell>{item.PolicyEffectiveDate}</TableCell>
              <TableCell>{item.LOB}</TableCell>
              <TableCell>{item.AgencyCode}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <>
      {userData && (
        <div id="customer-lookup-insured-name-table">
          <div>
            <b>Search results for "{insuredName}"</b>
          </div>
          <hr />
          {renderLoadingOrError()}
          {customer.data && renderNewExperience()}
        </div>
      )}
    </>
  );
};

export default CustomerLookup;
